import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import type {ActionMenuItem} from '@Components/action-menu/action-menu.types';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';
import {ClickableDiv} from '@Components/clickable-div';
import {ColorTypes} from '@Components/controlled-list/components/controlled-list-item-type-7';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import {useEventWizardCoverPhoto, useEventWizardIsCoverPhotoPreviewLoading} from '@Components/event-wizard/event-wizard.hooks';
import {PreviewShortcutTarget} from '@Components/event-wizard/preview/preview.types';
import {usePreviewShortcutClicked} from '@Components/event-wizard/preview/use-preview-shortcut-clicked';
import {onRemoveMedia} from '@Components/event-wizard/event-wizard-image-video/event-wizard-image-video.helper';
import {EventWizardCoverMenuOpener, MediaTypes} from '@Components/event-wizard/event-wizard-image-video/event-wizard-image-video.types';
import {useEventWizardImageVideoSources} from '@Components/event-wizard/event-wizard-image-video/use-event-wizard-image-video-sources';
import {EventWizardCoverMenu} from '@Components/event-wizard/event-wizard-image-video/components/event-wizard-cover-menu/event-wizard-cover-menu';
import type {EventWizardCoverMenuSection} from '@Components/event-wizard/event-wizard-image-video/components/event-wizard-cover-menu/event-wizard-cover-menu.types';
import {EventWizardCoverMenuSectionId} from '@Components/event-wizard/event-wizard-image-video/components/event-wizard-cover-menu/event-wizard-cover-menu.types';
import {ImageBlurBackground} from '@Components/event-wizard/image-blur-background';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {noop} from '@Utils/general.util';
import {GA4EventName, GA4EventParamName, trackGA4CustomEvent} from '@Libraries/ga-events';
import type {CoverTileMenuItem} from './event-wizard-image-video-tile.types';
import {COVER_MENU_OPENER_GA4_SOURCES, COVER_TILE_MENU_ITEM_GA4_TYPES, COVER_TILE_SIZE, CoverTileMenuItemId} from './event-wizard-image-video-tile.types';
import styles from './event-wizard-image-video-tile.module.scss';

export function EventWizardImageVideoTile(): ReactElement {
  const coverPhoto = useEventWizardCoverPhoto();
  const isCoverPhotoPreviewLoading = useEventWizardIsCoverPhotoPreviewLoading();
  const {loadingMediaType, canEditCoverDesign, openMediaSource, openCoverDesignEditor} = useEventWizardImageVideoSources();
  const hasCoverPhoto = !!coverPhoto.uploadedUrl || isCoverPhotoPreviewLoading;
  const isOpeningPicker = loadingMediaType !== null;
  /** What opened the menu; null while it is closed. */
  const [coverMenuOpener, setCoverMenuOpener] = useState<EventWizardCoverMenuOpener | null>(null);
  const isOpenedFromPreview = coverMenuOpener === EventWizardCoverMenuOpener.PREVIEW;
  const tileRef = useRef<HTMLDivElement>(null);

  usePreviewShortcutClicked(PreviewShortcutTarget.COVER, (): void => {
    setCoverMenuOpener(EventWizardCoverMenuOpener.PREVIEW);
  });

  useEffect(() => {
    if (isOpenedFromPreview) {
      tileRef.current?.scrollIntoView({block: 'nearest', behavior: 'smooth'});
    }
  }, [isOpenedFromPreview]);

  /** Reports the pick to GA4 before acting on it. */
  const getMenuItem = (item: CoverTileMenuItem): ActionMenuItem => {
    return {
      ...item,
      onClick: (): void => {
        trackGA4CustomEvent(GA4EventName.EVENT_WIZARD_CHANGE_COVER, {
          [GA4EventParamName.SOURCE]: COVER_MENU_OPENER_GA4_SOURCES[coverMenuOpener ?? EventWizardCoverMenuOpener.TILE],
          [GA4EventParamName.TYPE]: COVER_TILE_MENU_ITEM_GA4_TYPES[item.id],
        });
        item.onClick();
      },
    };
  };

  const getEditDesignMenuItems = (): ActionMenuItem[] => {
    if (!canEditCoverDesign) {
      return [];
    }
    return [getMenuItem({id: CoverTileMenuItemId.EDIT_DESIGN, icon: 'icon-pencil', text: window.i18next.t('pmwjs_edit_designs'), onClick: openCoverDesignEditor})];
  };

  const getSourceMenuItems = (): ActionMenuItem[] => {
    return [
      getMenuItem({
        id: CoverTileMenuItemId.AI_DESIGNS,
        icon: 'icon-sparkle-filled',
        text: window.i18next.t('pmwjs_design_templates_sentence_case'),
        textClasses: 'content-ai',
        iconClasses: 'content-ai',
        onClick: (): void => {
          openMediaSource(MediaTypes.TEMPLATES);
        },
      }),
      getMenuItem({
        id: CoverTileMenuItemId.MY_DESIGNS,
        icon: 'icon-folder-2',
        text: window.i18next.t('pmwjs_my_designs'),
        onClick: (): void => {
          openMediaSource(MediaTypes.MY_DESIGNS);
        },
      }),
      getMenuItem({
        id: CoverTileMenuItemId.UPLOADS,
        icon: 'icon-upload',
        text: window.i18next.t('pmwjs_upload_image'),
        onClick: (): void => {
          openMediaSource(MediaTypes.UPLOAD);
        },
      }),
    ];
  };

  const getRemoveMenuItems = (): ActionMenuItem[] => {
    if (!hasCoverPhoto) {
      return [];
    }
    return [
      getMenuItem({
        id: CoverTileMenuItemId.REMOVE,
        icon: 'icon-delete',
        text: window.i18next.t('pmwjs_remove'),
        color: ColorTypes.danger,
        textClasses: styles.coverMenuRemoveText,
        onClick: onRemoveMedia,
      }),
    ];
  };

  /** With a cover, the sources are offered as replacements, under a header that says so. */
  const getMenuSections = (): EventWizardCoverMenuSection[] => {
    return [
      {id: EventWizardCoverMenuSectionId.EDIT, header: null, items: getEditDesignMenuItems()},
      {id: EventWizardCoverMenuSectionId.SOURCES, header: hasCoverPhoto ? window.i18next.t('pmwjs_replace_with') : null, items: getSourceMenuItems()},
      {id: EventWizardCoverMenuSectionId.REMOVE, header: null, items: getRemoveMenuItems()},
    ];
  };

  const getMenu = (isMobile: boolean): ReactElement => {
    return <EventWizardCoverMenu isMobile={isMobile} sections={getMenuSections()} />;
  };

  const getPickerLoader = (): ReactElement => {
    return <CircularProgressLoader size={LOADER_SIZE.SMALL} color={LOADER_COLOR.DARK} classes="-no-margin" />;
  };

  /** Covers the thumbnail from the click on a source until its picker is on screen. */
  const getThumbnailLoadingVeil = (): ReactElement | null => {
    if (!isOpeningPicker) {
      return null;
    }
    return <div className={`${styles.coverTileLoadingVeil} radius-12`}>{getPickerLoader()}</div>;
  };

  /** A pencil over the cover on hover, on focus and while the menu is open, saying the tile opens a way to change it. */
  const getEditOverlay = (): ReactElement => {
    return (
      <div className={`${styles.coverTileEditOverlay} radius-12`}>
        <Icon icon="icon-pencil" size={IconSize.SIZE_ICON_20} className={styles.coverTileEditIcon} />
      </div>
    );
  };

  /** With no cover, a placeholder holds the thumbnail's place: the same grey box and icon the wizard's preview shows. */
  const getThumbnail = (): ReactElement => {
    if (!hasCoverPhoto) {
      return (
        <div className={`${styles.coverTilePlaceholder} flex-center radius-12`}>
          <Icon icon="icon-image" size={IconSize.SIZE_ICON_20} className={styles.coverTilePlaceholderIcon} />
        </div>
      );
    }
    return (
      <ImageBlurBackground
        maxHeight={COVER_TILE_SIZE}
        maxWidth={COVER_TILE_SIZE}
        url={coverPhoto.uploadedUrl}
        isLoading={isCoverPhotoPreviewLoading}
        containerClass={`${styles.coverTileImage} radius-12`}
      />
    );
  };

  const getTile = (): ReactElement => {
    return (
      <ClickableDiv
        onClick={noop}
        aria-haspopup="menu"
        aria-busy={isOpeningPicker}
        aria-label={window.i18next.t(hasCoverPhoto ? 'pmwjs_change' : 'pmwjs_add_image')}
        className={`${styles.coverTile} ${coverMenuOpener !== null ? styles.coverTileMenuOpen : ''} ${isOpenedFromPreview ? styles.coverTileHighlighted : ''} _position-relative radius-12 _cursor-pointer`}
      >
        {getThumbnail()}
        {hasCoverPhoto ? getEditOverlay() : null}
        {getThumbnailLoadingVeil()}
      </ClickableDiv>
    );
  };

  return (
    <div ref={tileRef}>
      <div className="flex-row-align-center spacing-m-b-1">
        <Text val={window.i18next.t('pmwjs_cover')} size={TextSize.SM} bold />
      </div>
      <Dropdown
        isControlled
        open={coverMenuOpener !== null}
        onOpen={(): void => {
          setCoverMenuOpener(EventWizardCoverMenuOpener.TILE);
        }}
        onClose={(): void => {
          setCoverMenuOpener(null);
        }}
        selector={getTile()}
        popup={getMenu(false)}
        modalContent={getMenu(true)}
        modalTitle={window.i18next.t('pmwjs_cover')}
        alignment={ALIGNMENT_TYPE.BOTTOM_START}
        popupClassName={styles.coverMenuPopup}
        popUpHasCustomWidth
        doCloseOnInnerClick
        showModalForSmallScreen
        isSmallScreenModalBottomSheet
      />
    </div>
  );
}
